import { createFileRoute, Link } from "@tanstack/react-router";
import { Brain, Bell, Share2, ArrowRight, Coffee, Plane, Mic, Network } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/hero-card.webp";
import memory from "@/assets/ai-abstract.jpg";
import nudge from "@/assets/float-profile.png";
import profile from "@/assets/phone-card.jpg";

export const Route = createFileRoute("/vi/solutions/founders")({
  head: () => ({
    meta: [
      { title: "Hệ điều hành liên hệ cho nhà sáng lập — banana.cards" },
      { name: "description", content: "Nhà đầu tư, đối tác, khách hàng, ứng viên — Banana ghi nhớ mọi cuộc trò chuyện và nhắc bạn đúng ngày họ trở nên quan trọng." },
      { property: "og:title", content: "Hệ điều hành liên hệ cho nhà sáng lập — banana.cards" },
      { property: "og:description", content: "Nhà đầu tư, đối tác, khách hàng — Banana không bao giờ để bạn đánh mất một mối quan hệ quan trọng." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/solutions/founders", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Cho nhà sáng lập"
      title="Mọi cuộc trò chuyện"
      italic="đều quan trọng."
      description="Mạng lưới của bạn là lợi thế cạnh tranh. Banana là bộ não thứ hai ghi nhớ mọi ly cà phê, lời giới thiệu và DM — và báo cho bạn khi đến lúc theo dõi."
      heroImage={hero}
      metrics={[
        { v: "3.200+", l: "Liên hệ trung bình được quản lý" },
        { v: "92%", l: "Lời giới thiệu được phản hồi trong 48 giờ" },
        { v: "1", l: "Hồ sơ trên mọi kênh" },
      ]}
      bullets={[
        { icon: Brain, title: "AI ghi nhớ cho mọi người", desc: "Ghi chú, voice memo, cập nhật LinkedIn, email và cuộc họp — tự động tóm tắt thành một hồ sơ sống cho mỗi liên hệ." },
        { icon: Bell, title: "Nhắc trước khi quá muộn", desc: "Banana theo dõi vòng gọi vốn, đổi việc và chuỗi im lặng — và nhắc bạn liên lạc đúng thời điểm." },
        { icon: Share2, title: "Một hồ sơ, mọi kênh", desc: "Thẻ NFC, QR, Apple Wallet, chữ ký email, LinkedIn — cập nhật một lần, đồng bộ khắp nơi. Luôn là bản pitch mới nhất." },
      ]}
      deepDives={[
        {
          eyebrow: "Ghi nhớ",
          title: "Đừng mở đầu bằng",
          italic: "\"nhắc tôi mình gặp ở đâu nhỉ?\"",
          description: "Mọi cuộc họp được phiên âm, mọi voice memo được tóm tắt, mọi email được hiển thị — gói gọn thành một đoạn ngắn về người bạn sắp nói chuyện.",
          points: [
            "AI ghi chú cho Zoom, Meet & Teams",
            "Voice memo → ghi chú tìm kiếm được",
            "Lịch sử email & lịch theo từng liên hệ",
            "Tóm tắt trước cuộc họp gửi vào inbox 15 phút trước",
          ],
          image: memory,
        },
        {
          eyebrow: "Nhắc nhở",
          title: "Luôn được nhớ đến,",
          italic: "mà không làm phiền.",
          description: "Đặt nhịp giữ liên lạc cho từng người — hàng tuần với đồng sáng lập, hàng quý với angel, hàng năm với đồng nghiệp cũ — Banana sẽ nổi họ lên đúng lúc, kèm bản thảo tin nhắn sẵn sàng.",
          points: [
            "Nhịp theo dõi theo từng liên hệ",
            "Cảnh báo đổi việc & vòng gọi vốn",
            "Nhắc sinh nhật & kỷ niệm",
            "Tin nhắn AI soạn theo giọng văn của bạn",
          ],
          image: nudge,
          reverse: true,
        },
        {
          eyebrow: "Chia sẻ",
          title: "Chỉ cần một liên kết",
          italic: "để giới thiệu bản thân.",
          description: "Hồ sơ Banana là bản pitch, lịch, portfolio và danh thiếp — gói trong một lần chạm hoặc QR. Thêm vào chữ ký email một lần và bạn sẽ không bao giờ phải dán lại liên kết Calendly.",
          points: [
            "Thẻ NFC bằng kim loại, gỗ, vàng & PVC",
            "Pass Apple & Google Wallet",
            "Trình tạo chữ ký email",
            "Liên kết có thương hiệu với tên miền riêng",
          ],
          image: profile,
        },
      ]}
      howItWorks={[
        { title: "Nhập mạng lưới", desc: "Kết nối Gmail, LinkedIn CSV, iCloud và Calendly — Banana khử trùng lặp và làm giàu dữ liệu ngầm." },
        { title: "Chọn nhịp liên lạc", desc: "Gắn nhãn nhà đầu tư, khách hàng, bạn bè. Đặt tần suất muốn giữ liên lạc cho từng nhãn." },
        { title: "Để AI lắng nghe", desc: "Thêm trợ lý ghi chú vào cuộc gọi. Chuyển tiếp voice memo. Banana xây ký ức cho bạn." },
        { title: "Theo dõi đúng lúc", desc: "Mở bản tin hàng ngày \"ai cần liên lạc\" và gửi tin nhắn đã soạn chỉ trong hai lần chạm." },
      ]}
      capabilities={[
        "AI ghi chú cuộc họp (50+ ngôn ngữ)",
        "Voice memo → CRM trong một bước",
        "Gắn thẻ nhà đầu tư & cap-table",
        "Làm giàu dữ liệu LinkedIn & email",
        "Pass Apple & Google Wallet",
        "Trình tạo chữ ký email",
        "Liên kết đặt lịch",
        "Mã hóa đầu-cuối trên ghi chú",
      ]}
      faq={[
        { q: "Đây có chỉ là sổ địa chỉ xịn không?", a: "Không — Banana là người quản lý quan hệ chủ động. Sổ địa chỉ lưu dữ liệu; Banana theo dõi tín hiệu (vai trò mới, gọi vốn mới, 90 ngày im lặng) và báo bạn khoảnh khắc đáng để chạm điểm, kèm bản thảo tin nhắn theo giọng văn của bạn." },
        { q: "Làm sao AI ghi nhớ luôn chính xác?", a: "Mỗi cuộc họp bạn dùng trợ lý ghi chú Banana, mỗi voice memo bạn chuyển tiếp, và mỗi luồng email bạn gắn thẻ đều được tóm tắt vào hồ sơ liên hệ. Ghi chú cũ không bị ghi đè — chúng xếp chồng theo thời gian, để bạn cuộn lại toàn bộ lịch sử." },
        { q: "Tôi có thể giữ vài liên hệ riêng tư khỏi đội ngũ không?", a: "Có. Nhà sáng lập điều hành công ty trên Banana có thể đánh dấu liên hệ là cá nhân — chúng nằm ngoài CRM đội ngũ, ngoài pipeline chung, và chỉ bạn thấy được. Danh sách nhà đầu tư thường được giữ ở đây." },
        { q: "Dữ liệu của tôi ra sao nếu tôi rời đi?", a: "Bạn sở hữu nó. Xuất mọi liên hệ, ghi chú và bản ghi dưới dạng JSON hoặc CSV bất cứ lúc nào. Ghi chú được mã hóa đầu-cuối và chúng tôi không bao giờ huấn luyện AI trên dữ liệu của bạn." },
        { q: "Có ứng dụng iOS / Android không?", a: "Có — và đó là nơi hầu hết nhà sáng lập sống. App native cho iPhone và Android phủ chạm-để-chia-sẻ, voice note, nhắc đẩy và bản tin hàng ngày \"ai cần liên lạc\"." },
      ]}
      outro={{
        title: "Mạng lưới là",
        italic: "lợi thế không công bằng.",
        description: "Đừng để nó mục nát trong ba CRM, một ứng dụng Notes và 14.000 kết nối LinkedIn. Mang nó về nhà.",
      }}
      afterFaq={<FounderUseCases />}
    />
  );
}

function FounderUseCases() {
  const items = [
    { i: Coffee, t: "Gọi vốn", d: "Theo dõi mọi cuộc trò chuyện với nhà đầu tư, đánh dấu nóng vs lạnh, được nhắc trước vòng kế tiếp một tuần.", to: "/vi/platform/pipelines" },
    { i: Plane, t: "Hội nghị & công tác", d: "Chạm thẻ tại sàn triển lãm, đồng bộ CRM trước khi lên máy bay, theo dõi trước khi jetlag.", to: "/vi/platform/events" },
    { i: Mic, t: "Podcast & báo chí", d: "Lưu mọi host, nhà báo & nhà sản xuất trong một danh sách có nhãn. Pitch lại khi có tin tức.", to: "/vi/platform/crm" },
    { i: Network, t: "Tuyển dụng & cố vấn", d: "Giữ một danh sách private các ứng viên mơ ước. Nhắc hàng năm cho đến khi đúng thời điểm.", to: "/vi/platform/contacts" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tình huống cho nhà sáng lập</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Mọi chiếc mũ bạn đội, <em className="italic">đều được phục vụ.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Khám phá <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}